import * as yup from "yup";

import {
  MakeNonProportionalPayment,
  MakeNonProportionalPaymentVariables,
} from "_graphql/mutation/__generated__/MakeNonProportionalPayment";
import { getSumOFNPPayments, wrapClick } from "utils";
import { useAppLocation, useAppSearch } from "_graphql/cache/auth";
import { useEffect, useMemo } from "react";
import { useNonProportionalPayments, useTreaty } from "hooks/data/use-treaties";

import { Modal } from "components";
import { NonProportionalPaymentForm } from "components/forms";
import _ from "lodash";
import { makeNonProportionalPayment } from "_graphql/visal/mutations/payment";
import numeral from "numeral";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";

const formSchema = yup.object().shape({
  payment_type: yup.string().required("Payment type is required"),
  auto_payment_receipt: yup.boolean(),
  layer_limit: yup.string().required("Layer limit is required"),
  date_of_receipt: yup.string().required("Layer limit is required"),
  // installment_type: yup.string().required("Installment type is required"),
  payment_from: yup.object().shape({
    bank_name: yup.string().required("Bank name is required"),
    cheque_number: yup.string().when("$payment_type", {
      is: (val: string) => val === "Cheque",
      then(schema) {
        return schema.required("Cheque number is required");
      },
      otherwise(schema) {
        return schema.notRequired();
      },
    }),
    date_on_cheque: yup.string().when("$payment_type", {
      is: (val: string) => val === "Cheque",
      then(schema) {
        return schema.required("Date on cheque is required");
      },
      otherwise(schema) {
        return schema.notRequired();
      },
    }),
  }),
  offer_payment_comment: yup.string(),
  payment_to: yup.string().required("Payment to is required"),
  payment_details: yup.object().shape({
    payment_amount: yup
      .number()
      .required("Payment amount is required")
      .min(0, "Payment amount should not be less than 0")
      .test(
        "max-payment",
        "Payment amount should not be more than amount to pay",
        function (value) {
          const {
            amountToPay,
            payment_details: {
              conversion: { rate },
            },
          } = this.options.context as any;
          return value <= amountToPay;
        }
      ),
    currency: yup.string().required("Currency is required"),
    conversion: yup.object().shape({
      addExchangeRate: yup.boolean(),
      rate: yup.number().required("Rate is required"),
      currency: yup.string().when("addExchangeRate", {
        is: true,
        then(schema) {
          return schema.required("Currency is required");
        },
        otherwise(schema) {
          return schema.notRequired();
        },
      }),
    }),
  }),
  amountToPay: yup.number().required("Amount to pay is required"),
});

type Props = {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
};

const CreateNonProportionalPaymentCOntainer = ({ open, setOpen }: Props) => {
  const { params } = useAppLocation();
  const searchParams = useAppSearch();

  const { loading: fetchingTreaty, treaty } = useTreaty({
    treatyId: params?.treaty,
  });
  const limitsData: any[] = JSON.parse(treaty?.layer_limit || "[]");

  const { payments, loading: fetchingPayments } = useNonProportionalPayments({
    filter: {
      treaty_id: params?.treaty,
    },
  });
  const [create, { loading }] = useMutation<
    MakeNonProportionalPayment,
    MakeNonProportionalPaymentVariables
  >(makeNonProportionalPayment, {
    refetchQueries: ["GetNonProportionalTreatyPayments"],
  });
  const form = useFormik({
    initialValues: {
      payment_type: "",
      auto_payment_receipt: false,
      amountToPay: 0,
      amountToPayOriginal: 0,
      layer_limit: "",
      date_of_receipt: "",
      installment_type: "",
      payment_from: {
        bank_name: "",
        cheque_number: "",
        date_on_cheque: "",
      },
      offer_payment_comment: "",
      payment_to: "",
      payment_details: {
        payment_amount: 0,
        currency: "",
        conversion: {
          rate: 1,
          currency: "",
          addExchangeRate: false,
        },
      },
    },
    validationSchema: formSchema,
    onSubmit: (value, helpers) => {
      const layer = limitsData.at(Number(value.layer_limit) - 1);
      create({
        variables: {
          payment: {
            payment_amount: value.payment_details.payment_amount,
            payment_status:
              value.payment_details.payment_amount == 0
                ? "UNPAID"
                : value.amountToPay > value.payment_details.payment_amount
                  ? "PARTPAYMENT"
                  : " PAID",
            auto_payment_receipt: value.auto_payment_receipt,
            installment_type: parseInt(value.layer_limit),
            payment_comment: value.offer_payment_comment,
            treaty_account_id: layer?.uuid,
            date_of_receipt: value.date_of_receipt,
            treaty_id: params?.treaty,
            uuid: layer?.uuid,
            payment_details: JSON.stringify({
              currency: value.payment_details.currency,
              payment_type: value.payment_type,
              payment_from: {
                cheque_number: value.payment_from.cheque_number
                  ? value.payment_from.cheque_number
                  : "N/A",
                bank_name: value.payment_from.bank_name,
                date_on_cheque: value.payment_from.date_on_cheque
                  ? value.payment_from.date_on_cheque
                  : "N/A",
              },
              payment_to: value.payment_to,
              conversion: {
                rate: value.payment_details.conversion.rate,
                currency:
                  value?.payment_details?.currency ||
                  value.payment_details.conversion.currency ||
                  "N/A",
                addExchangeRate:
                  value.payment_details.conversion.addExchangeRate,
              },
            }),
          },
        },
      })
        .then((res) => {
          if (res.data?.makeNonProportionalPayment) {
            toast.success("Payment recorded successfully");
            helpers.resetForm();
            setOpen?.(false);
          } else {
            toast.error("Payment could not be recorded");
          }
        })
        .catch((error) => {
          toast.error("Payment could not be recorded");
        });
    },
  });
  const percentage: number =
    treaty?.treaty_participants?.reduce((acc: number, cur) => {
      const value =
        numeral(cur?.treaty_participation_percentage || 0).value() || 0;
      return acc + value;
    }, 0) ?? 0;

  const actualPercentage = percentage / limitsData.length;

  useEffect(() => {
    if (!form.values.layer_limit) return;
    console.log("Layer limit changed Start", {
      treaty_np_payments: payments,
      uuid: form.values.layer_limit,
      installment_type: form.values.layer_limit,
      treaty_np_payment_id: searchParams.id,
    });
    const oldPayments = getSumOFNPPayments({
      treaty_np_payments: payments,
      uuid: form.values.layer_limit,
      installment_type: form.values.layer_limit,
      treaty_np_payment_id: searchParams.id,
    });

    const sumOfAllLayersMandD =
      limitsData.reduce((acc: number, curr: any) => {
        return numeral(curr?.m_and_d_premium || "0")
          .add(acc)
          .value();
      }, 0) / _.first(limitsData)?.installment_type;

    const amountToPayRaw =
      numeral(actualPercentage)
        .divide(100)
        .multiply(sumOfAllLayersMandD)
        .subtract(oldPayments)
        .value() || 0;

    const convertedAmount =
      amountToPayRaw <= 0
        ? 0
        : numeral(amountToPayRaw)
            .multiply(form.values.payment_details.conversion.rate)
            .value();

    // Only set if different
    if (form.values.amountToPay !== convertedAmount) {
      form.setFieldValue("amountToPay", convertedAmount);
      form.setFieldValue("amountToPayOriginal", amountToPayRaw);
      form.setFieldValue("payment_details.payment_amount", 0); // if needed
    }
  }, [
    form.values.layer_limit,
    form.values.installment_type,
    form.values.payment_details.conversion.rate,
    actualPercentage, // make sure this is stable (see below)
    limitsData,
    searchParams?.id,
  ]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={fetchingTreaty || fetchingPayments}
      title="Make payment"
      description="Make a payment to the treaty."
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Please wait ..." : "Make Payment"}
          </button>
        </>
      )}
    >
      {/* {JSON.stringify(form.values, null, 2)} */}
      <NonProportionalPaymentForm
        form={form}
        treaty={treaty}
        amountToPay={form.values.amountToPay}
      />
    </Modal>
  );
};

export default CreateNonProportionalPaymentCOntainer;
